import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@material-ui/core';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogComponent } from 'common/components/index';
import messages from 'common/components/EmployeeProfile/messages';
import commonMessages from 'common/messages/messages';
import PersonalSection from './PersonalSection';
import AccountSection from './AccountSection';
import ContactSection from './ContactSection';
import { MaritalStatuses } from 'common/constants/personConstants';
import * as yup from 'yup';
import {
  exemptionsNumberSchema,
  idSchema,
  literalNameSchema,
  nameSchema,
} from 'components/EmployeeForm/schemas/schemas';
import {
  futureOrTodayDateSchema,
  pastDateSchema,
  pastOrTodayDateSchema,
} from 'common/validationSchemas/dateSchemas';
import { addressSchema } from 'common/validationSchemas/addressSchema';
import { IEmployeeDto, IEmployeeDtoImt } from 'common/interfaces/employee';
import { phoneSchema } from 'common/validationSchemas/phoneSchema';
import { getEmailErrorMessage, getRequiredErrorMessage } from 'common/utils/validation';
import { passHintSchema } from 'common/validationSchemas/passwordSchemas';
import inputErrors from 'common/messages/inputErrors';

interface IEditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  onSubmit: (data: IEmployeeDto) => void;
  profile: IEmployeeDtoImt;
}

const EditProfileModal = (props: IEditProfileModalProps): JSX.Element => {
  const { isOpen, onSubmit, onClose, profile, isLoading } = props;

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      id: yup.string(),
      firstName: literalNameSchema.required(getRequiredErrorMessage),
      lastName: literalNameSchema.required(getRequiredErrorMessage),
      preferredName: nameSchema,
      gender: yup
        .string()
        .nullable()
        .required(getRequiredErrorMessage),
      birthday: pastDateSchema,
      maritalStatus: yup
        .string()
        .nullable()
        .required(getRequiredErrorMessage),
      exemptionsNumber: exemptionsNumberSchema,
      federalWithholding: exemptionsNumberSchema,
      address: addressSchema,
      hireDate: pastOrTodayDateSchema.required(getRequiredErrorMessage),
      terminationDate: futureOrTodayDateSchema,
      email: yup
        .string()
        .trim()
        .email(getEmailErrorMessage)
        .required(getRequiredErrorMessage),
      employeeIdentifier: idSchema.required(getRequiredErrorMessage),
      phone: phoneSchema.required(getRequiredErrorMessage).typeError(getRequiredErrorMessage),
      passwordHint: passHintSchema,
      verbalPassword: passHintSchema,
      pinCode: yup
        .string()
        .nullable()
        .min(4, () => inputErrors.pinCodeLengthError),
    });
  }, []);

  const initialValues = useMemo(() => {
    const initValues: Record<string, any> = {
      image: null,
      firstName: '',
      lastName: '',
      preferredName: '',
      gender: '',
      birthday: null,
      maritalStatus: MaritalStatuses.SINGLE,
      exemptionsNumber: 0,
      federalWithholding: 0,
      address: {
        city: '',
        country: null,
        line1: '',
        line2: '',
        state: null,
        zipCode: '',
      },
      phone: '',
      hireDate: null,
      terminationDate: null,
      email: '',
      posDiscountPercentage: 0,
      barcode: '',
      passwordHint: '',
      verbalPassword: '',
      pinCode: null,
      employeeIdentifier: '',
    };

    return initValues;
  }, []);

  const formMethods = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });
  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    if (profile) {
      const convertedProfile = profile.toJS();
      const { deleted, phones, ...formData } = convertedProfile;

      reset({
        ...initialValues,
        ...formData,
        exemptionsNumber: formData.exemptionsNumber || 0,
        federalWithholding: formData.federalWithholding || 0,
      });
    }
  }, [profile, reset, initialValues]);

  const formId = 'edit-profile-form';

  return (
    <DialogComponent
      isOpen={isOpen}
      size="md"
      title={<FormattedMessage {...messages.editMyProfile} />}
      submitBtnTitle={<FormattedMessage {...commonMessages.saveBtn} />}
      onClose={onClose}
      loading={isLoading}
      formId={formId}
    >
      <FormProvider {...formMethods}>
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <PersonalSection />
            </Grid>

            <Grid item xs={12}>
              <ContactSection />
            </Grid>

            <Grid item xs={12}>
              <AccountSection />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </DialogComponent>
  );
};

export default EditProfileModal;
